// Branded QR codes: a scannable QR in the Legend Sports Cave brand shape — copper
// modules on a warm cream ground, wrapped in the **LSC shield crest** silhouette (the
// logo's own outline). Used for every QR the app mints — Whiteboard from Device (ADR
// 0095), Cave Lobby "Preview on my device" (ADR 0134), and guest invites (ADR
// 0044 / LSC-275) — so they all carry the mark (ADR 0141, restyled by ADR 0151).
//
// The frame is the ACTUAL logo silhouette: we trace the transparent logo's outline
// from its alpha channel at runtime (per-row min/max opaque x → the shield's left and
// right profile) and stroke that in copper, so the QR literally takes the shape of our
// crest — peaked top, full-width side points, a pointed base — no hand-authored path to
// drift from the artwork.
//
// Scannability: the QR is generated at error-correction level **H** on a high-contrast
// copper/cream palette, keeps its full quiet zone + finder patterns, and is the largest
// square that fits **inside** the shield's inner copper edge (centred), so a crest edge
// never crosses the code. Best-effort + graceful: under jsdom/SSR, or if the logo/canvas
// can't be read, it returns the plain (still level-H, still copper-on-cream) QR — never
// throws, so a QR always renders.

import QRCode from "qrcode";

// The transparent logo lives in web/public and is served same-origin, so drawing it
// onto the canvas (and reading its pixels for the silhouette) does not taint it.
const LOGO_SRC = "/legend-logo.png";

// Brand palette (matches the reference mock): a deep copper dark enough to scan
// against the warm cream ground, used for BOTH the QR modules and the crest frame.
const COPPER = "#a85a28";
const CREAM = "#f1e7d3";

let logoPromise: Promise<HTMLImageElement> | null = null;
function loadLogo(): Promise<HTMLImageElement> {
  if (!logoPromise) {
    logoPromise = loadImage(LOGO_SRC);
  }
  return logoPromise;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Never hang the QR on a stuck asset load (real-browser safety net).
    const timer = setTimeout(() => reject(new Error("image load timeout")), 5000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error("image load failed"));
    };
    img.src = src;
  });
}

// The logo silhouette: for every image row, the leftmost/rightmost opaque pixel (the
// shield's outer edge at that height), plus the opaque bounding box. Traced once and
// cached — the artwork is stable, so this is a single getImageData on first use.
interface Silhouette {
  rowMin: number[];
  rowMax: number[];
  minRow: number;
  maxRow: number;
  minCol: number;
  bw: number;
  bh: number;
}

let shieldPromise: Promise<Silhouette | null> | null = null;
function loadShield(): Promise<Silhouette | null> {
  if (!shieldPromise) {
    shieldPromise = (async () => {
      const logo = await loadLogo();
      const w = logo.width;
      const h = logo.height;
      if (!w || !h) return null;
      const oc = document.createElement("canvas");
      oc.width = w;
      oc.height = h;
      const g = oc.getContext("2d");
      if (!g) return null;
      g.drawImage(logo, 0, 0);
      // Same-origin asset → not tainted → getImageData is allowed.
      const d = g.getImageData(0, 0, w, h).data;
      const alpha = 32; // ignore faint anti-aliasing / glow
      const rowMin = new Array<number>(h).fill(-1);
      const rowMax = new Array<number>(h).fill(-1);
      let minRow = h;
      let maxRow = -1;
      let minCol = w;
      let maxCol = -1;
      for (let y = 0; y < h; y++) {
        let lo = -1;
        let hi = -1;
        const base = y * w * 4;
        for (let x = 0; x < w; x++) {
          if (d[base + x * 4 + 3] > alpha) {
            if (lo < 0) lo = x;
            hi = x;
          }
        }
        rowMin[y] = lo;
        rowMax[y] = hi;
        if (lo >= 0) {
          if (y < minRow) minRow = y;
          if (y > maxRow) maxRow = y;
          if (lo < minCol) minCol = lo;
          if (hi > maxCol) maxCol = hi;
        }
      }
      if (maxRow < 0) return null;
      return { rowMin, rowMax, minRow, maxRow, minCol, bw: maxCol - minCol || 1, bh: maxRow - minRow || 1 };
    })().catch(() => null);
  }
  return shieldPromise;
}

export interface BrandedQrOptions {
  /** Output size in px (square). Default 240. */
  size?: number;
  /** Quiet-zone width in QR modules. Default 1. */
  margin?: number;
}

/**
 * brandedQrDataUrl returns a PNG data URL of `text` as a copper-on-cream QR code
 * wrapped in the LSC shield crest (the logo's own silhouette). Falls back to the plain
 * (copper-on-cream, level-H) QR if compositing isn't possible.
 */
export async function brandedQrDataUrl(text: string, opts: BrandedQrOptions = {}): Promise<string> {
  const size = opts.size ?? 240;
  const margin = opts.margin ?? 1;
  const base = await QRCode.toDataURL(text, {
    errorCorrectionLevel: "H",
    margin,
    width: size,
    color: { dark: COPPER, light: CREAM },
  });
  if (typeof document === "undefined") return base;
  // jsdom (the test env) can fake a canvas 2D context but never decodes images, so
  // the compositing below would hang on Image.onload; skip it and return the plain
  // (still level-H) QR. Real browsers fall through and get the branded version.
  if (typeof navigator !== "undefined" && /\bjsdom\b/i.test(navigator.userAgent)) return base;
  try {
    const shield = await loadShield();
    if (!shield) return base; // couldn't read the artwork → plain QR

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return base;

    // Warm cream ground. The QR's own light modules are the same cream, so the code
    // square blends seamlessly into the crest field.
    ctx.fillStyle = CREAM;
    ctx.fillRect(0, 0, size, size);

    const frameW = Math.max(3, size * 0.045);
    const pad = frameW / 2 + 1;
    const scale = Math.min((size - 2 * pad) / shield.bw, (size - 2 * pad) / shield.bh);
    const scaledW = shield.bw * scale;
    const scaledH = shield.bh * scale;
    const offX = (size - scaledW) / 2;
    const offY = (size - scaledH) / 2;
    const cx = size / 2;
    const mapX = (col: number) => offX + (col - shield.minCol) * scale;
    const mapY = (row: number) => offY + (row - shield.minRow) * scale;

    // Trace the crest: down the right profile, up the left profile (sampled every few
    // rows — round joins keep the peaked top / side points / base point crisp).
    ctx.beginPath();
    let started = false;
    const step = 3;
    for (let r = shield.minRow; r <= shield.maxRow; r += step) {
      if (shield.rowMax[r] < 0) continue;
      const x = mapX(shield.rowMax[r]);
      const y = mapY(r);
      if (started) ctx.lineTo(x, y);
      else {
        ctx.moveTo(x, y);
        started = true;
      }
    }
    for (let r = shield.maxRow; r >= shield.minRow; r -= step) {
      if (shield.rowMin[r] < 0) continue;
      ctx.lineTo(mapX(shield.rowMin[r]), mapY(r));
    }
    ctx.closePath();
    ctx.lineWidth = frameW;
    ctx.strokeStyle = COPPER;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.stroke();

    // Largest CENTRED square that fits inside the crest's inner copper edge. For each
    // output row, the centred half-width available inside the shield (minus the frame
    // stroke + a small gap); grow the square from the vertical centre until an edge
    // would cross a crest wall. Centring balances the peaked top against the base point.
    const gap = frameW / 2 + size * 0.01;
    const half = new Array<number>(size + 1).fill(0);
    const yTop = Math.max(0, Math.floor(offY));
    const yBot = Math.min(size, Math.ceil(offY + scaledH));
    for (let y = yTop; y <= yBot; y++) {
      const r = Math.round(shield.minRow + (y - offY) / scale);
      if (r < shield.minRow || r > shield.maxRow || shield.rowMin[r] < 0) {
        half[y] = 0;
        continue;
      }
      half[y] = Math.max(0, Math.min(cx - mapX(shield.rowMin[r]), mapX(shield.rowMax[r]) - cx) - gap);
    }
    const midY = offY + scaledH / 2;
    let hh = 0;
    for (let h = 1; h < scaledH / 2; h++) {
      let ok = true;
      const lo = Math.floor(midY - h);
      const hi = Math.ceil(midY + h);
      for (let y = lo; y <= hi; y++) {
        if ((half[y] || 0) < h) {
          ok = false;
          break;
        }
      }
      if (ok) hh = h;
      else break;
    }
    const qrSide = Math.max(1, Math.floor(hh * 2));
    // If the crest leaves too little room to scan, fall back to the plain QR rather
    // than emit an unreadably small code.
    if (qrSide < size * 0.34) return base;

    const qx = Math.round(cx - qrSide / 2);
    const qy = Math.round(midY - qrSide / 2);
    const qrImg = await loadImage(base);
    ctx.drawImage(qrImg, qx, qy, qrSide, qrSide);

    return canvas.toDataURL("image/png");
  } catch {
    return base; // logo/canvas hiccup → still a scannable QR
  }
}
